import type { ReactElement, ReactNode } from 'react';
import { useState, Children, useEffect } from 'react';
import { useDebounce, useKeyPressEvent } from 'react-use';
import { CloseAction, TextInput } from '@hotjar/hotsauce';

import { useTranslationWithPrefix } from '@hotjar/translations';
import { EmptyState } from '@hotjar/webapp/components/EmptyState';
import NoSearchResultsPNG from '@hotjar/shared-assets/images/no-search-results.png';

const TRANSLATION_PREFIX = 'common:search';

export interface SearchProviderProps {
  children: ReactNode;
  placeholder: string;
  className?: string;
  debounce?: number;
  onSearchChanged(searchValue: string): void;
}

export function SearchProvider({
  children = [],
  placeholder,
  className,
  debounce = 300,
  onSearchChanged,
}: SearchProviderProps): ReactElement {
  const { t } = useTranslationWithPrefix(TRANSLATION_PREFIX);
  const [searchValue, setSearchValue] = useState<string>('');

  const [, cancel] = useDebounce(() => onSearchChanged(searchValue), debounce, [searchValue]);

  const handleReset = () => {
    setSearchValue('');
    onSearchChanged('');
  };

  useKeyPressEvent('Escape', handleReset);

  useEffect(() => () => cancel(), [cancel]);

  return (
    <>
      <TextInput
        id="search-input"
        name={placeholder}
        value={searchValue}
        placeholder={placeholder}
        onChange={(e) => setSearchValue(e.target.value)}
        icon="search"
        enterKeyHint="search"
        className={className}
        actionContent={
          searchValue && <CloseAction onClick={handleReset} description={t('clear')} />
        }
      />
      {searchValue && Children.count(children) === 0 ? (
        <EmptyState
          title={t('no-matches-found', { searchValue })}
          subtitle={t('suggestion')}
          img={NoSearchResultsPNG}
        />
      ) : (
        <>{children}</>
      )}
    </>
  );
}
